import type { CSSProperties } from 'react';
import { useActivityStatus } from '../../hooks/useActivityStatus';

const ACTIVE_GREEN = '#22C55E';

/**
 * "Active now" / "Active 5m ago" / "Active today" / "Active this week".
 *
 * Renders nothing when there is nothing to show: hidden, blocked, never active,
 * or older than a week. Callers can drop it in without a guard.
 *
 * Styled inline throughout: src/index.css is precompiled Tailwind, so utility
 * classes that are not already in it render nothing.
 *
 * `variant="dot"` is the avatar badge: a green dot, only while active now. Put it
 * inside a `position: relative` avatar wrapper.
 */
export function ActivityStatus({
  userId,
  live = false,
  variant = 'text',
  style,
  ringColor = '#0D0D0D',
}: {
  userId?: string | number | null;
  live?: boolean;
  variant?: 'text' | 'dot';
  style?: CSSProperties;
  /** Border around the dot, matching whatever the avatar sits on. */
  ringColor?: string;
}) {
  const { label, isActiveNow } = useActivityStatus(userId, { live });
  if (!label) return null;

  if (variant === 'dot') {
    if (!isActiveNow) return null;
    return (
      <span
        aria-label="Active now"
        title="Active now"
        style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 12, height: 12, borderRadius: '50%',
          background: ACTIVE_GREEN, border: `2px solid ${ringColor}`,
          ...style,
        }}
      />
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 12, lineHeight: 1.2, whiteSpace: 'nowrap',
        color: isActiveNow ? ACTIVE_GREEN : '#8e8e8e',
        fontWeight: isActiveNow ? 600 : 400,
        ...style,
      }}
    >
      {isActiveNow && (
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ACTIVE_GREEN, flexShrink: 0 }} />
      )}
      {label}
    </span>
  );
}
